import { Link, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2MetaStrip,
  V2Badge,
  V2Table,
  LoadingState,
  StatePanel,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { JOURNAL_STATUS_LABEL, SETTLEMENT_METHOD_LABEL } from '../../lib/types/finance.js';
import type {
  TreasuryAccountListResponse,
  TreasuryAllocation,
  TreasuryTransaction,
} from '../../lib/types/finance.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import {
  amountColumnHeader,
  formatRialFa,
  journalHref,
  openItemHref,
  treasuryTxnStatusLabel,
} from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { PartyLabel } from './PartyLabel.js';

interface AllocationRow extends TreasuryAllocation {
  readonly id: string;
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Finance.dc.html — treasury receipt/payment detail; card
 *   language from Sakhtar Cloud ERP v2.dc.html finTreasury (V2CardHeader +
 *   V2MetaStrip, Jalali dates)
 * canonical pattern: TRANSACTION_DETAIL — meta strip card + allocation table
 * status: KIT_CONFORMANT — GET .../finance/treasury/transactions/:id returns
 *   journal + optional settlement. Allocation amounts are server-returned.
 *   The treasury account NAME comes from GET .../treasury/accounts (a
 *   separate permission, treasury.account.read) and is simply omitted when
 *   that call fails — the detail endpoint itself returns only the id
 *   (serializeTreasuryTransactionSummary, subledger-serialize.ts:79-95). No
 *   description / counterpart-account name is returned by the API, so none
 *   is shown.
 * state reference: ready · permission_denied · not_found · network_error
 */
export function TreasuryTransactionDetailPage() {
  const { unit, label } = useAmountDisplay();
  const { slug, treasuryTransactionId } = useParams<{
    slug: string;
    treasuryTransactionId: string;
  }>();
  const base = slug ? tenantBasePath(slug) : '';
  const txn = useApiGet<TreasuryTransaction>(
    slug && treasuryTransactionId
      ? `${base}/api/v1/finance/treasury/transactions/${treasuryTransactionId}`
      : null,
    [slug, treasuryTransactionId],
  );
  const accounts = useApiGet<TreasuryAccountListResponse>(
    slug ? `${base}/api/v1/finance/treasury/accounts` : null,
    [slug],
  );
  const accountName =
    accounts.status === 'ready' && txn.status === 'ready'
      ? accounts.data.items.find((a) => a.treasuryAccountId === txn.data.treasuryAccountId)?.name
      : undefined;

  if (!slug || !treasuryTransactionId) return null;

  const allocationColumns: readonly V2TableColumn<AllocationRow>[] = [
    {
      key: 'kind',
      header: 'نوع قلم',
      render: (row) => (row.openItemKind === 'ar' ? 'دریافتنی' : 'پرداختنی'),
    },
    {
      key: 'item',
      header: 'قلم باز',
      render: (row) =>
        row.openItemKind === 'ar' || row.openItemKind === 'ap' ? (
          <Link to={openItemHref(base, row.openItemKind, row.openItemId)}>مشاهده</Link>
        ) : (
          <span>—</span>
        ),
    },
    {
      key: 'amount',
      header: amountColumnHeader('مبلغ تخصیص', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.amountRial, unit)}</span>,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="sk-breadcrumb">
        <span>مالی</span>
        <span>/</span>
        <Link to={`${base}/finance/treasury`}>خزانه</Link>
        <span>/</span>
        <b>{txn.status === 'ready' ? txn.data.kindLabel : 'گردش'}</b>
      </div>

      {txn.status === 'loading' ? <LoadingState /> : null}
      {txn.status === 'error' ? (
        <StatePanel
          variant={
            txn.error.status === 403
              ? 'permission_denied'
              : txn.error.status === 404
                ? 'not_found'
                : 'network_error'
          }
          title={
            txn.error.status === 403
              ? 'دسترسی ندارید'
              : txn.error.status === 404
                ? 'پیدا نشد'
                : 'قابل نمایش نیست'
          }
        />
      ) : null}

      {txn.status === 'ready' ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ margin: 0, fontSize: 'var(--sk-fs-2xl)', fontWeight: 700 }}>
              {txn.data.kindLabel}
            </h1>
            <V2Badge tone={txn.data.status === 'posted' ? 'success' : 'neutral'}>
              {treasuryTxnStatusLabel(txn.data.status)}
            </V2Badge>
          </div>
          <V2Card>
            <V2CardHeader icon="invoice" title="مشخصات گردش" subtitle={txn.data.kindLabel} />
            <V2MetaStrip
              items={[
                {
                  label: 'تاریخ',
                  value: <span className="sk-num">{displayJalaliDate(txn.data.businessDate)}</span>,
                },
                {
                  label: 'مبلغ',
                  value: (
                    <span className="sk-num">
                      {formatRialFa(txn.data.amountRial, unit)} {label}
                    </span>
                  ),
                },
                ...(accountName ? [{ label: 'حساب خزانه', value: accountName }] : []),
                {
                  label: 'طرف‌حساب',
                  value: txn.data.partyId ? (
                    <PartyLabel slug={slug} partyId={txn.data.partyId} />
                  ) : (
                    '—'
                  ),
                },
                {
                  label: 'روش تسویه',
                  value: txn.data.settlementMethod
                    ? SETTLEMENT_METHOD_LABEL[txn.data.settlementMethod]
                    : '—',
                },
                ...(txn.data.bankReference
                  ? [
                      {
                        label: 'مرجع بانکی',
                        value: <span className="ltr sk-num">{txn.data.bankReference}</span>,
                      },
                    ]
                  : []),
                {
                  label: 'شمارهٔ منبع',
                  value: (
                    <span className="ltr sk-num" style={{ fontFamily: 'var(--sk-font-mono)' }}>
                      {txn.data.sourceReference}
                    </span>
                  ),
                },
                {
                  label: 'سند حسابداری',
                  value: (
                    <Link to={journalHref(base, txn.data.originatingJournalId)}>مشاهدهٔ سند</Link>
                  ),
                },
                {
                  label: 'وضعیت سند خزانه',
                  value: txn.data.journal
                    ? (JOURNAL_STATUS_LABEL[txn.data.journal.status] ?? 'وضعیت ثبت‌شده')
                    : '—',
                },
              ]}
            />
          </V2Card>
          {txn.data.settlement && txn.data.settlement.allocations.length > 0 ? (
            <V2Card>
              <V2CardHeader
                icon="layers"
                title="تخصیص به اقلام باز"
                subtitle={`${txn.data.settlement.allocations.length.toLocaleString('fa-IR')} قلم`}
              />
              <V2Table
                columns={allocationColumns}
                rows={txn.data.settlement.allocations.map((row) => ({
                  ...row,
                  id: row.allocationId,
                }))}
                zebra
              />
            </V2Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
